// Клиентская проверка файлов ПЕРЕД отправкой на сервер. Нужна, потому что:
// - drag&drop игнорирует атрибут accept (можно бросить любой тип, папку, пустой файл);
// - выбранный файл может стать недоступным (удалён/заблокирован/это директория) — поймаем чтением.
// Возвращает либо валидные файлы, либо ключ i18n с параметрами для понятного сообщения.

/** Предел размера одного загружаемого документа (сделки, аттестации) — тот же, что принимает сервер. */
export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;
export const MAX_UPLOAD_MB = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));

export interface UploadConstraints {
  /** Разрешённые расширения; могут быть с ведущей точкой (".pdf") или без ("pdf"). */
  readonly extensions: readonly string[];
  readonly maxBytes: number;
}

export type UploadValidation =
  | { readonly ok: true; readonly files: File[] }
  | { readonly ok: false; readonly messageKey: string; readonly params?: Record<string, unknown> };

const stripDot = (ext: string): string => ext.replace(/^\./, '').toLowerCase();

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

const PREFIX = 'app.company.accreditation';

export async function validateUploadFiles(
  files: File[],
  { extensions, maxBytes }: UploadConstraints,
): Promise<UploadValidation> {
  const allowed = extensions.map(stripDot).filter(Boolean);

  for (const file of files) {
    if (allowed.length > 0 && !allowed.includes(extensionOf(file.name))) {
      return {
        ok: false,
        messageKey: `${PREFIX}.errType`,
        params: { types: allowed.map((ext) => ext.toUpperCase()).join(', ') },
      };
    }
    // Пустой размер = пустой файл или папка (у директории size === 0) — отправлять нечего.
    if (file.size === 0) {
      return { ok: false, messageKey: `${PREFIX}.errEmpty` };
    }
    if (file.size > maxBytes) {
      return {
        ok: false,
        messageKey: `${PREFIX}.errTooLarge`,
        params: { max: Math.round(maxBytes / (1024 * 1024)) },
      };
    }
    // Пробуем реально прочитать первый байт: ловим «выбран, но недоступен» до отправки,
    // иначе ошибка вылезла бы как непонятный сетевой сбой в середине загрузки.
    try {
      await file.slice(0, 1).arrayBuffer();
    } catch {
      return { ok: false, messageKey: `${PREFIX}.errUnreadable` };
    }
  }

  return { ok: true, files };
}
